import React, { useContext, useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { FormContext } from '../context'
import { cn } from '@s4l/utility-css'

interface ICheckboxProps {
  name: string
  size?: 's' | 'l'
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  multiple?: boolean
  trueValue?: string | boolean
  falseValue?: string | boolean
}

const Checkbox = ({
  name,
  size = 's',
  defaultChecked,
  onChange,
  disabled,
  multiple,
  trueValue = true,
  falseValue = false,
}: ICheckboxProps) => {
  const { form, setFormValue, setFormArrayValue, removeFormArrayValue, setInputInitialized } =
    useContext(FormContext)
  const hasDefaultValue = useRef(0)

  const hasToSync = useRef(true)
  const [checked, setChecked] = useState(defaultChecked)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked)
    onChange?.(e.target.checked)

    hasToSync.current = false

    if (multiple) {
      if (e.target.checked) {
        setFormArrayValue?.(e.target.name, trueValue, true)
      } else {
        removeFormArrayValue?.(e.target.name, trueValue, true)
      }
    } else {
      setFormValue?.(e.target.name, e.target.checked ? trueValue : falseValue, true)
    }
  }

  useEffect(() => {
    const value = form[name]

    if (!hasToSync.current) {
      hasToSync.current = true
      return
    }

    const hasBeenInitialized = hasDefaultValue.current > 1

    if (defaultChecked === undefined && value === undefined) {
      setFormValue?.(name, multiple ? [] : falseValue, true)
    } else if (defaultChecked !== undefined && !hasBeenInitialized) {
      if (multiple) {
        setFormArrayValue?.(name, defaultChecked ? trueValue : undefined, false)
      } else {
        setFormValue?.(name, defaultChecked ? trueValue : falseValue, false)
      }
      setChecked(defaultChecked)
      hasDefaultValue.current += 1
      setInputInitialized(`${name}-${trueValue}`, true)
    } else if (typeof value === 'boolean' && !hasBeenInitialized) {
      setChecked(value)
    } else if (typeof value === 'string' && !hasBeenInitialized) {
      setChecked(value === trueValue)
    } else if (value === undefined) {
      setChecked(false)
      setFormValue?.(name, multiple ? [] : falseValue, true)
    } else if (multiple && Array.isArray(value) && typeof trueValue === 'string') {
      setChecked(value.includes(trueValue))
    }
  }, [
    form,
    name,
    defaultChecked,
    setFormValue,
    multiple,
    trueValue,
    falseValue,
    setFormArrayValue,
    setInputInitialized,
  ])

  useEffect(() => {
    setInputInitialized(`${name}-${trueValue}`, false)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      name={name}
      type="checkbox"
      className={cn(styles.checkbox, {
        [styles.big]: size === 'l',
      })}
      checked={checked ?? false}
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

Checkbox.displayName = 'Form.Checkbox'
export default Checkbox
